/**
 * Que hacer cuando se guarda una fila en el detalle de la lista de precios.
 *
 * - El precio en la lista se guarda solo, al cambiarlo ('lista').
 * - La oferta (precio + fechas) NO se guarda campo por campo: se edita en
 *   pantalla y se guarda una sola vez con el boton de la fila ('oferta'). Asi
 *   el orden de llenado no importa y se muestra un solo aviso.
 * - 'limpiar' retira la oferta de la fila.
 *
 * Devuelve { save, payload, aviso }:
 *  - save=false: no se llama al servidor; aviso (si hay) explica que falta.
 *  - payload sin claves de oferta: el servidor deja la oferta como esta.
 */

// Vaciar un campo numerico puede dejar '', null, undefined o NaN segun el
// navegador: todos significan "vacio".
export function isEmptyValue(value) {
  return value === '' || value === null || value === undefined || Number.isNaN(value)
}

// La fila tiene una oferta guardada en el servidor (no solo escrita en pantalla).
export function hasSavedOffer(item) {
  return Boolean(item.offer_status) && item.offer_status !== 'sin_oferta'
}

const SIN_OFERTA = { offer_price: null, offer_starts_at: null, offer_ends_at: null }

const aviso = (titulo, texto) => ({ save: false, payload: null, aviso: { titulo, texto } })

export function planRowSave(item, origen) {
  const listPrice = { fixed_price: item.fixed_price }

  // El precio de lista se guarda solo; la oferta (guardada o a medias) no se toca.
  if (origen === 'lista') {
    return { save: true, payload: listPrice, aviso: null }
  }

  // Boton "limpiar oferta" de la fila.
  if (origen === 'limpiar') {
    return { save: true, payload: { ...listPrice, ...SIN_OFERTA }, aviso: null }
  }

  // origen 'oferta': boton "guardar oferta" de la fila.
  if (isEmptyValue(item.offer_price)) {
    // Precio vaciado sobre una oferta ya guardada: es retirarla.
    if (hasSavedOffer(item)) {
      return { save: true, payload: { ...listPrice, ...SIN_OFERTA }, aviso: null }
    }

    return aviso('Falta el precio ofertado', 'Escribe el precio ofertado para guardar la oferta.')
  }

  if (!item.offer_starts_at) {
    return aviso('Falta la fecha de inicio', 'Elige la fecha "Desde" para guardar la oferta.')
  }

  if (item.offer_ends_at && item.offer_ends_at < item.offer_starts_at) {
    return aviso('Fechas invertidas', 'La fecha "Hasta" no puede ser anterior a "Desde".')
  }

  if (!(Number(item.offer_price) > 0)) {
    return aviso('Precio ofertado inválido', 'El precio ofertado debe ser mayor a cero.')
  }

  // Una "oferta" igual o mas cara que el precio de lista no es oferta: el
  // cliente pagaria lo mismo o mas y el PDF mostraria ahorro Q0.
  if (!isEmptyValue(item.fixed_price) && Number(item.offer_price) >= Number(item.fixed_price)) {
    return aviso(
      'La oferta no es menor al precio de lista',
      `El precio ofertado debe ser menor al precio en la lista (Q${Number(item.fixed_price).toFixed(2)}).`,
    )
  }

  return {
    save: true,
    payload: {
      ...listPrice,
      offer_price: item.offer_price,
      offer_starts_at: item.offer_starts_at,
      offer_ends_at: item.offer_ends_at || null,
    },
    aviso: null,
  }
}
